import {
  KeySignature,
  KEY_C, KEY_G, KEY_F, KEY_D, KEY_Bb, KEY_Eb, KEY_A, KEY_Ab, KEY_B,
  KEY_Am, KEY_Dm, KEY_Em, KEY_Cm, KEY_Gm,
  getKeyRoot, midiToAbcNote,
} from './melodyGeneration'

export interface RomanNumeral {
  symbol: string
  scaleDegree: number
  quality: 'major' | 'minor' | 'diminished' | 'dominant7'
  semitones: number[]
}

export interface ChordVoicing {
  midi: number[]
  trebleAbc: string
  bassAbc: string
}

export interface GeneratedProgression {
  key: KeySignature
  chords: RomanNumeral[]
  voicings: ChordVoicing[]
  abc: string
}

export interface HarmonicTierConfig {
  keys: KeySignature[]
  chordPool: RomanNumeral[]
  progressionLength: number
}

const MAJOR_NUMERALS: RomanNumeral[] = [
  { symbol: 'I',    scaleDegree: 0, quality: 'major',      semitones: [0, 4, 7] },
  { symbol: 'ii',   scaleDegree: 2, quality: 'minor',      semitones: [0, 3, 7] },
  { symbol: 'iii',  scaleDegree: 4, quality: 'minor',      semitones: [0, 3, 7] },
  { symbol: 'IV',   scaleDegree: 5, quality: 'major',      semitones: [0, 4, 7] },
  { symbol: 'V',    scaleDegree: 7, quality: 'major',      semitones: [0, 4, 7] },
  { symbol: 'vi',   scaleDegree: 9, quality: 'minor',      semitones: [0, 3, 7] },
  { symbol: 'vii°', scaleDegree: 11, quality: 'diminished', semitones: [0, 3, 6] },
]

const MINOR_NUMERALS: RomanNumeral[] = [
  { symbol: 'i',    scaleDegree: 0, quality: 'minor',      semitones: [0, 3, 7] },
  { symbol: 'ii°',  scaleDegree: 2, quality: 'diminished', semitones: [0, 3, 6] },
  { symbol: 'III',  scaleDegree: 3, quality: 'major',      semitones: [0, 4, 7] },
  { symbol: 'iv',   scaleDegree: 5, quality: 'minor',      semitones: [0, 3, 7] },
  { symbol: 'V',    scaleDegree: 7, quality: 'major',      semitones: [0, 4, 7] },
  { symbol: 'VI',   scaleDegree: 8, quality: 'major',      semitones: [0, 4, 7] },
  { symbol: 'vii°', scaleDegree: 11, quality: 'diminished', semitones: [0, 3, 6] },
]

const V7: RomanNumeral = { symbol: 'V7', scaleDegree: 7, quality: 'dominant7', semitones: [0, 4, 7, 10] }

const SECONDARY_DOMINANTS_MAJOR: RomanNumeral[] = [
  { symbol: 'V/V',  scaleDegree: 2, quality: 'major', semitones: [0, 4, 7] },
  { symbol: 'V/vi', scaleDegree: 4, quality: 'major', semitones: [0, 4, 7] },
  { symbol: 'V/ii', scaleDegree: 9, quality: 'major', semitones: [0, 4, 7] },
  { symbol: 'V/IV', scaleDegree: 0, quality: 'major', semitones: [0, 4, 7] },
]

function numeralsForKey(key: KeySignature): RomanNumeral[] {
  return key.mode === 'major' ? MAJOR_NUMERALS : MINOR_NUMERALS
}

function findNumeral(pool: RomanNumeral[], symbol: string): RomanNumeral {
  return pool.find(n => n.symbol === symbol)!
}

const HARMONIC_TIERS: HarmonicTierConfig[] = [
  {
    keys: [KEY_C, KEY_G, KEY_F],
    chordPool: [],
    progressionLength: 4,
  },
  {
    keys: [KEY_C, KEY_G, KEY_F, KEY_D, KEY_Bb],
    chordPool: [],
    progressionLength: 5,
  },
  {
    keys: [KEY_C, KEY_G, KEY_F, KEY_D, KEY_Bb, KEY_Eb, KEY_Am, KEY_Dm, KEY_Em],
    chordPool: [],
    progressionLength: 6,
  },
  {
    keys: [KEY_C, KEY_G, KEY_F, KEY_D, KEY_Bb, KEY_Eb, KEY_A, KEY_Am, KEY_Dm, KEY_Em, KEY_Cm, KEY_Gm],
    chordPool: [],
    progressionLength: 7,
  },
  {
    keys: [KEY_C, KEY_G, KEY_F, KEY_D, KEY_Bb, KEY_Eb, KEY_A, KEY_Ab, KEY_B, KEY_Am, KEY_Dm, KEY_Em, KEY_Cm, KEY_Gm],
    chordPool: [],
    progressionLength: 8,
  },
]

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

function getAvailableChords(key: KeySignature, tier: number): RomanNumeral[] {
  const base = numeralsForKey(key)
  if (tier <= 2) {
    const simple = ['I', 'IV', 'V', 'i', 'iv', 'V']
    if (tier === 2) simple.push('ii', 'vi', 'ii°', 'VI')
    return base.filter(n => simple.includes(n.symbol))
  }
  if (tier === 3) {
    return [...base, V7]
  }
  if (tier === 4) {
    if (key.mode === 'major') return [...base, V7, ...SECONDARY_DOMINANTS_MAJOR.slice(0, 2)]
    return [...base, V7]
  }
  if (key.mode === 'major') return [...base, V7, ...SECONDARY_DOMINANTS_MAJOR]
  return [...base, V7]
}

function generateProgressionChords(key: KeySignature, tier: number, length: number): RomanNumeral[] {
  const available = getAvailableChords(key, tier)
  const tonic = key.mode === 'major'
    ? findNumeral(MAJOR_NUMERALS, 'I')
    : findNumeral(MINOR_NUMERALS, 'i')
  const dominant = findNumeral(available, tier >= 3 ? 'V7' : 'V') ?? findNumeral(available, 'V')

  const chords: RomanNumeral[] = [tonic]

  for (let i = 1; i < length - 1; i++) {
    const prev = chords[i - 1]
    const pool = available.filter(c => c.symbol !== prev.symbol)
    chords.push(pickRandom(pool))
  }

  if (dominant && chords[chords.length - 1].symbol !== dominant.symbol) {
    chords.push(dominant)
  }
  chords.push(tonic)

  while (chords.length > length) {
    chords.splice(Math.floor(chords.length / 2), 1)
  }

  return chords
}

function voiceChord(numeral: RomanNumeral, keyRoot: number, prevVoicing: ChordVoicing | null, key: KeySignature): ChordVoicing {
  const root = (keyRoot + numeral.scaleDegree) % 12
  const bassMidi = 36 + root + (root < 0 ? 12 : 0)
  const adjustedBass = bassMidi >= 36 && bassMidi <= 52 ? bassMidi : bassMidi + 12

  const trebleNotes = numeral.semitones.map(s => {
    let midi = 60 + ((root + s) % 12)
    if (midi < 60) midi += 12
    return midi
  })

  if (prevVoicing) {
    const prevTreble = prevVoicing.midi.slice(1)
    const prevCenter = prevTreble.reduce((a, b) => a + b, 0) / prevTreble.length
    const adjusted = trebleNotes.map(m => {
      while (m - prevCenter > 6) m -= 12
      while (prevCenter - m > 6) m += 12
      if (m < 55) m += 12
      if (m > 76) m -= 12
      return m
    })
    adjusted.sort((a, b) => a - b)

    return {
      midi: [adjustedBass, ...adjusted],
      trebleAbc: `[${adjusted.map(m => midiToAbcNote(m, key)).join('')}]`,
      bassAbc: midiToAbcNote(adjustedBass, key),
    }
  }

  trebleNotes.sort((a, b) => a - b)
  return {
    midi: [adjustedBass, ...trebleNotes],
    trebleAbc: `[${trebleNotes.map(m => midiToAbcNote(m, key)).join('')}]`,
    bassAbc: midiToAbcNote(adjustedBass, key),
  }
}

export function generateProgression(difficultyLevel: number): GeneratedProgression {
  const tierIdx = Math.max(0, Math.min(4, Math.round(difficultyLevel) - 1))
  const tierConfig = HARMONIC_TIERS[tierIdx]
  const key = pickRandom(tierConfig.keys)
  const length = tierConfig.progressionLength
  const chords = generateProgressionChords(key, tierIdx + 1, length)
  const keyRoot = getKeyRoot(key)

  const voicings: ChordVoicing[] = []
  for (let i = 0; i < chords.length; i++) {
    voicings.push(voiceChord(chords[i], keyRoot, voicings[i - 1] ?? null, key))
  }

  let abc = `X:1\nM:${chords.length}/4\nL:1/4\nK:${key.abc}\n`
  abc += `V:1 clef=treble\n`
  abc += voicings.map(v => v.trebleAbc).join(' ') + '|\n'
  abc += `V:2 clef=bass\n`
  abc += voicings.map(v => v.bassAbc).join(' ') + '|\n'

  return { key, chords, voicings, abc }
}

export function tierForHarmonicLevel(level: number): HarmonicTierConfig {
  const idx = Math.max(0, Math.min(4, Math.round(level) - 1))
  return HARMONIC_TIERS[idx]
}

export function normalizeRomanNumeral(input: string): string {
  return input.trim()
    .replace(/\u00B0/g, '°')
    .replace(/o$/i, '°')
    .replace(/\s+/g, '')
}

export function gradeRomanNumerals(
  expected: RomanNumeral[],
  answers: string[],
): { correct: boolean[]; score: number } {
  const correct = expected.map((chord, i) => {
    const answer = normalizeRomanNumeral(answers[i] ?? '')
    return answer === chord.symbol
  })
  const score = correct.filter(Boolean).length / correct.length
  return { correct, score }
}
